import { z } from "zod";

import { type ViewColumnDef } from "@moment/api-collab/api-types";

import { type JsonPathLiteral } from "..";
import { type TopLevelQuerySpec, topLevelQuerySpecSchema } from "../operatorsV2/QuerySpec";
import { type TableMutation, hydrateTableMutation, rawTableMutationSchema } from "./TableMutation";

//
// Action types.
//

export type Action =
	| MutationAction
	| QueryRegistrationAction
	| ColumnAppendAction
	| ColumnDeleteAction
	| ColumnRenameAction;

export interface QueryRegistrationAction {
	type: "queryRegistration";
	intentId: string;
	desiredStartEpoch: number;
	querySpec: TopLevelQuerySpec;
}

export interface MutationAction {
	type: "mutation";
	intentId: string;
	table: string;
	desiredApplyEpoch: number;
	mutation: TableMutation<unknown>;
}

export interface ColumnAppendAction {
	type: "columnAppend";
	intentId: string;
	table: string;
	desiredApplyEpoch: number;
	column: ViewColumnDef;
}

export interface ColumnDeleteAction {
	type: "columnDelete";
	intentId: string;
	table: string;
	desiredApplyEpoch: number;
	column: ViewColumnDef;
}

export interface ColumnRenameAction {
	type: "columnRename";
	intentId: string;
	table: string;
	desiredApplyEpoch: number;
	oldColumnAccessorKey: JsonPathLiteral;
	newHeader: string;
	newAccessorKey: JsonPathLiteral;
}

export const hydrateAction = (raw: RawAction): Action => {
	switch (raw.type) {
		case "mutation":
			return {
				type: "mutation",
				intentId: raw.intentId,
				table: raw.table,
				desiredApplyEpoch: raw.desiredApplyEpoch,
				mutation: hydrateTableMutation(raw.mutation),
			};
		case "queryRegistration":
			return {
				type: "queryRegistration",
				intentId: raw.intentId,
				desiredStartEpoch: raw.desiredStartEpoch,
				querySpec: raw.querySpec,
			};
	}
};

//
// Raw action types.
//

export const rawMutationActionSchema = z.object({
	type: z.literal("mutation"),
	intentId: z.string(),
	table: z.string(),
	desiredApplyEpoch: z.number(),
	mutation: rawTableMutationSchema,
});

export const rawRegisterQueryActionSchema = z.object({
	type: z.literal("queryRegistration"),
	intentId: z.string(),
	desiredStartEpoch: z.number(),
	querySpec: topLevelQuerySpecSchema,
});

export const rawActionSchema = z.discriminatedUnion("type", [
	rawMutationActionSchema,
	rawRegisterQueryActionSchema,
]);
export type RawAction = z.infer<typeof rawActionSchema>;

export const dehydrateAction = (action: Action): unknown => {
	switch (action.type) {
		case "queryRegistration":
			return action as unknown;
		case "mutation": {
			let raw: RawAction;
			switch (action.mutation.type) {
				case "INSERT":
					raw = {
						type: "mutation",
						intentId: action.intentId,
						table: action.table,
						desiredApplyEpoch: action.desiredApplyEpoch,
						mutation: {
							type: "INSERT",
							record: {
								id: action.mutation.recordId,
								value: action.mutation.record.getValue(),
							},
						},
					};
					break;
				case "UPDATE":
					raw = {
						type: "mutation",
						intentId: action.intentId,
						table: action.table,
						desiredApplyEpoch: action.desiredApplyEpoch,
						mutation: {
							type: "UPDATE",
							recordId: action.mutation.recordId,
							change: action.mutation.change,
						},
					};
					break;
				case "DELETE":
					raw = {
						type: "mutation",
						intentId: action.intentId,
						table: action.table,
						desiredApplyEpoch: action.desiredApplyEpoch,
						mutation: {
							type: "DELETE",
							recordId: action.mutation.recordId,
						},
					};
					break;
			}

			return raw as unknown;
		}
	}
};
